/**
 * ตั้งค่าเช็คลิสต์รับพนักงานใหม่ — HR ปรับรายการเทมเพลตได้เอง: เพิ่ม/ลบ/แก้ชื่อ/
 * บังคับหรือไม่/จัดลำดับ + เลือกว่าให้ระบบเช็คอัตโนมัติหรือติ๊กมือ.
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useOnboardingTemplate, useSaveOnboardingTemplate } from '../../hooks';
import {
  ONBOARDING_CATEGORIES,
  ONBOARDING_CATEGORY_LABELS,
  ONBOARDING_AUTO_RULES,
  EMPLOYEE_DOC_TYPES,
  EMPLOYEE_DOC_TYPE_LABELS,
  type OnboardingItem,
  type OnboardingCategory,
  type EmployeeDocType,
} from '@payroll/shared';
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';

const AUTO_RULE_LABELS: Record<string, string> = {
  salary_set: 'ตั้งฐานเงินเดือนแล้ว',
  department_set: 'มีแผนก + ตำแหน่งแล้ว',
  line_paired: 'ผูก LINE แล้ว',
  contract_signed: 'เซ็นสัญญาจ้างแล้ว',
};

function emptyItem(sortOrder: number): OnboardingItem {
  return {
    item_key: `item_${Date.now()}`,
    category: 'setting',
    label: '',
    doc_type: null,
    auto_check: null,
    required: true,
    sort_order: sortOrder,
    active: true,
    due_days: null,
  };
}

export function OwnerOnboardingSetup() {
  const { data: template, isLoading } = useOnboardingTemplate();
  const saveM = useSaveOnboardingTemplate();
  const toast = useToast();

  const [items, setItems] = useState<OnboardingItem[]>([]);

  useEffect(() => {
    if (template) setItems(template.map((it) => ({ ...it })));
  }, [template]);

  function updateItem(idx: number, patch: Partial<OnboardingItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function moveItem(idx: number, dir: -1 | 1) {
    setItems((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const a = prev[idx];
      const b = prev[target];
      if (!a || !b) return prev;
      const next = [...prev];
      next[idx] = b;
      next[target] = a;
      return next.map((it, i) => ({ ...it, sort_order: (i + 1) * 10 }));
    });
  }

  function addItem() {
    setItems((prev) => [...prev, emptyItem((prev.length + 1) * 10)]);
  }

  async function handleSave() {
    const cleaned = items
      .filter((it) => it.label.trim())
      .map((it, i) => ({ ...it, sort_order: (i + 1) * 10 }));
    if (cleaned.length === 0) {
      toast.error('ต้องมีอย่างน้อย 1 รายการ');
      return;
    }
    const keys = new Set<string>();
    for (const it of cleaned) {
      if (keys.has(it.item_key)) {
        toast.error(`รหัสรายการซ้ำ: ${it.item_key}`);
        return;
      }
      keys.add(it.item_key);
    }
    try {
      await saveM.mutateAsync(cleaned);
      toast.success('บันทึกเทมเพลตสำเร็จ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ตั้งค่าเช็คลิสต์รับพนักงานใหม่">
      <p className="text-sm text-gray-600 mb-1">
        กำหนดรายการที่ต้องทำให้ครบก่อนพนักงานใหม่จะ "onboard เสร็จ" — เอกสารที่ต้องส่ง / สิ่งที่ต้องตั้งค่า / เอกสารที่ต้องเซ็น
      </p>
      <p className="text-xs text-gray-400 mb-1">
        ติ๊ก "ให้ระบบเช็คอัตโนมัติ" = ระบบตรวจสอบเองว่าเสร็จหรือยัง (เช่น ส่งเอกสารแล้ว/ตั้งเงินเดือนแล้ว) — เว้นว่าง = HR ต้องติ๊กเองในหน้ารายคน
      </p>
      <p className="text-xs text-gray-400 mb-4">
        "ต้องเสร็จภายใน … วัน" นับจาก <span className="font-medium">วันเริ่มงาน</span> ของพนักงานคนนั้น — หน้าติดตามจะบอกว่าเหลืออีกกี่วันและใครเลยกำหนดแล้ว (เว้นว่าง = ไม่มีกำหนด)
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : (
        <div className="space-y-2 mb-4">
          {items.map((it, idx) => (
            <ItemRow
              key={it.item_key || idx}
              item={it}
              onChange={(patch) => updateItem(idx, patch)}
              onRemove={() => removeItem(idx)}
              onMoveUp={idx > 0 ? () => moveItem(idx, -1) : undefined}
              onMoveDown={idx < items.length - 1 ? () => moveItem(idx, 1) : undefined}
            />
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={addItem} className="btn-secondary flex-1">
          <Plus className="w-4 h-4" /> เพิ่มรายการ
        </button>
        <button onClick={handleSave} disabled={saveM.isPending} className="btn-cta flex-1">
          <Save className="w-4 h-4" /> {saveM.isPending ? 'กำลังบันทึก…' : 'บันทึกเทมเพลต'}
        </button>
      </div>
    </AdminLayout>
  );
}

function ItemRow({
  item,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  item: OnboardingItem;
  onChange: (patch: Partial<OnboardingItem>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const isDocCategory = item.category === 'document' || item.category === 'signature';

  function handleDocTypeChange(docType: EmployeeDocType | '') {
    if (!docType) {
      onChange({ doc_type: null });
      return;
    }
    // เลือกประเภทเอกสาร → เสนอ auto_check แบบ document:<type> ให้อัตโนมัติ (แก้เองได้)
    onChange({ doc_type: docType, auto_check: `document:${docType}` });
  }

  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="ชื่อรายการ เช่น ส่งสำเนาบัตรประชาชน"
          className="input flex-1"
        />
        <button onClick={onMoveUp} disabled={!onMoveUp} className="btn-secondary px-2 disabled:opacity-30">
          <ArrowUp className="w-4 h-4" />
        </button>
        <button onClick={onMoveDown} disabled={!onMoveDown} className="btn-secondary px-2 disabled:opacity-30">
          <ArrowDown className="w-4 h-4" />
        </button>
        <button onClick={onRemove} className="btn-secondary px-2 text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select
          value={item.category}
          onChange={(e) => onChange({ category: e.target.value as OnboardingCategory })}
          className="input"
        >
          {ONBOARDING_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {ONBOARDING_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm px-2">
          <input
            type="checkbox"
            checked={item.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            className="w-4 h-4"
          />
          บังคับ (ต้องทำก่อนถือว่าเสร็จ)
        </label>
      </div>

      {/* กำหนดวันครบ — ตัวที่ทำให้หน้าติดตามบอกได้ว่า "อีกกี่วันต้องเซ็น" */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600 whitespace-nowrap">ต้องเสร็จภายใน</span>
        <input
          type="number"
          min={0}
          max={365}
          value={item.due_days ?? ''}
          onChange={(e) => onChange({ due_days: e.target.value === '' ? null : Math.max(0, Math.min(365, Number(e.target.value))) })}
          placeholder="—"
          className="input w-24"
        />
        <span className="text-sm text-gray-600 whitespace-nowrap">วัน นับจากวันเริ่มงาน</span>
        <span className="text-xs text-gray-400">
          {item.due_days === null || item.due_days === undefined
            ? 'เว้นว่าง = ไม่มีกำหนด'
            : item.due_days === 0
              ? 'ครบกำหนดวันเริ่มงานพอดี'
              : `ครบกำหนดวันที่ ${item.due_days} หลังเริ่มงาน`}
        </span>
      </div>

      {isDocCategory && (
        <select
          value={item.doc_type ?? ''}
          onChange={(e) => handleDocTypeChange(e.target.value as EmployeeDocType | '')}
          className="input w-full"
        >
          <option value="">— เลือกประเภทเอกสาร (ถ้ามี) —</option>
          {EMPLOYEE_DOC_TYPES.map((t) => (
            <option key={t} value={t}>
              {EMPLOYEE_DOC_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      )}

      <select
        value={item.auto_check ?? ''}
        onChange={(e) => onChange({ auto_check: e.target.value || null })}
        className="input w-full"
      >
        <option value="">ติ๊กมือ (HR ติ๊กเอง)</option>
        {item.doc_type && (
          <option value={`document:${item.doc_type}`}>ให้ระบบเช็คอัตโนมัติ — ส่งเอกสารแล้ว ({EMPLOYEE_DOC_TYPE_LABELS[item.doc_type]})</option>
        )}
        {ONBOARDING_AUTO_RULES.map((r) => (
          <option key={r} value={r}>
            ให้ระบบเช็คอัตโนมัติ — {AUTO_RULE_LABELS[r] ?? r}
          </option>
        ))}
      </select>
    </div>
  );
}
